import { useState } from 'react';
import { Bell, CheckCheck, Pencil, Plus, Save, X } from 'lucide-react';

import { cn } from '../../utils/cn';
import { Button } from '../ui';

export type RollCallStatus = 'present' | 'late' | 'absent' | 'excused';

export interface RollCallStudent {
    id: string;
    name?: string;
    status: RollCallStatus | 'not_marked' | string;
    isTemporary?: boolean;
    checkedAt?: Date | string | null;
    lateArrivalTime?: Date | string | null;
    note?: string;
    lateNote?: string;
    absentNote?: string;
    excusedNote?: string;
}

export type RollCallDetail = { lateTime?: string; note?: string; lateNote?: string; absentNote?: string; excusedNote?: string };

const STATUS_OPTIONS: Array<{ value: RollCallStatus; label: string; active: string }> = [
    { value: 'present', label: 'Có mặt', active: 'bg-success-600 text-white' },
    { value: 'late', label: 'Muộn', active: 'bg-warning-500 text-white' },
    { value: 'absent', label: 'Vắng', active: 'bg-danger-600 text-white' },
    { value: 'excused', label: 'Có phép', active: 'bg-academy-600 text-white' },
];

// Mỗi trạng thái có ô ghi chú riêng trong dữ liệu.
const NOTE_FIELD: Record<RollCallStatus, 'note' | 'lateNote' | 'absentNote' | 'excusedNote'> = {
    present: 'note',
    late: 'lateNote',
    absent: 'absentNote',
    excused: 'excusedNote',
};

const NOTE_PLACEHOLDER: Record<RollCallStatus, string> = {
    present: 'Ghi chú',
    late: 'Lý do đi muộn',
    absent: 'Lý do vắng',
    excused: 'Lý do nghỉ',
};

const GRID = 'md:grid-cols-[minmax(9rem,1fr)_19.75rem_minmax(14rem,1.4fr)_4.75rem]';

const fieldClass = 'h-9 min-w-0 rounded border border-ink-300 bg-white px-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-200 disabled:cursor-not-allowed disabled:bg-ink-100';
const iconButton = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-ink-400 transition-colors disabled:cursor-not-allowed disabled:opacity-40';

const isMarked = (status: string): status is RollCallStatus => status in NOTE_FIELD;

// Date → "HH:mm" theo giờ máy.
export function toTimeValue(value: Date | string | null | undefined) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

export function countRollCall(students: readonly RollCallStudent[]) {
    const counts = { present: 0, late: 0, absent: 0, excused: 0, not_marked: 0 };
    for (const student of students) {
        counts[isMarked(student.status) ? student.status : 'not_marked'] += 1;
    }
    return counts;
}

export function noteOf(student: RollCallStudent) {
    return isMarked(student.status) ? student[NOTE_FIELD[student.status]] || '' : '';
}

function markedAtTitle(student: RollCallStudent) {
    if (!student.checkedAt || !isMarked(student.status)) return undefined;
    const date = new Date(student.checkedAt);
    if (Number.isNaN(date.getTime())) return undefined;
    return `Điểm danh lúc ${date.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}`;
}

interface RollCallTableProps {
    students: RollCallStudent[];
    canEdit: boolean;
    dirty: boolean;
    saving: boolean;
    saveDisabled: boolean;
    onSave: () => void;
    onStatusChange: (studentId: string, status: RollCallStatus) => void;
    onDetailChange: (studentId: string, detail: RollCallDetail) => void;
    onMarkAllPresent: () => void;
    onAddAlert: (student: RollCallStudent) => void;
    onRemoveMakeupStudent?: (studentId: string) => void;
    alertBusyKey?: string | null;
    alertDisabled?: boolean;
    onAddMakeupStudent?: () => void;
}

export function RollCallTable({
    students,
    canEdit,
    dirty,
    saving,
    saveDisabled,
    onSave,
    onStatusChange,
    onDetailChange,
    onMarkAllPresent,
    onAddAlert,
    onRemoveMakeupStudent,
    alertBusyKey,
    alertDisabled,
    onAddMakeupStudent,
}: RollCallTableProps) {
    const counts = countRollCall(students);
    const [editingNote, setEditingNote] = useState<string | null>(null);

    return (
        <section className="space-y-3" aria-label="Điểm danh học sinh">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-ink-600">
                    <span className="font-medium text-success-700">Có mặt {counts.present}</span>
                    {' · '}<span className="font-medium text-warning-700">Muộn {counts.late}</span>
                    {' · '}<span className="font-medium text-danger-700">Vắng {counts.absent}</span>
                    {' · '}<span className="font-medium text-academy-700">Có phép {counts.excused}</span>
                    {' · '}<span className={cn('font-medium', counts.not_marked ? 'text-ink-900' : 'text-ink-500')}>Chưa điểm danh {counts.not_marked}</span>
                </p>
                <div className="flex flex-wrap items-center gap-2">
                    {dirty ? <span className="text-xs font-medium text-warning-700">Có thay đổi chưa lưu</span> : null}
                    {canEdit ? (
                        <Button
                            variant="secondary"
                            onClick={onMarkAllPresent}
                            disabled={counts.not_marked === 0}
                            icon={<CheckCheck className="h-4 w-4" />}
                        >
                            Cả lớp có mặt
                        </Button>
                    ) : null}
                    <Button onClick={onSave} loading={saving} disabled={saveDisabled} icon={<Save className="h-4 w-4" />}>
                        Lưu điểm danh
                    </Button>
                </div>
            </div>

            {students.length === 0 ? (
                <div className="rounded-lg border border-dashed border-ink-300 px-4 py-8 text-center text-sm text-ink-500">
                    Lớp học này chưa có học sinh nào.
                </div>
            ) : (
                <div className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                    <div className={cn('hidden bg-ink-50 px-3 py-2 text-xs font-medium text-ink-600 md:grid md:gap-4', GRID)}>
                        <span>Học sinh</span>
                        <span>Trạng thái</span>
                        <span>Ghi chú</span>
                        <span className="sr-only">Thao tác</span>
                    </div>
                    {students.map(student => {
                        const name = student.name || 'Chưa có tên';
                        const alertKey = `student:${student.id}`;
                        const marked = isMarked(student.status);
                        const note = noteOf(student);
                        const noteField = marked ? NOTE_FIELD[student.status as RollCallStatus] : null;
                        const editing = editingNote === student.id && Boolean(noteField);
                        const showLateTime = student.status === 'late';
                        // Trên điện thoại chỉ hiện dòng ghi chú khi có nội dung.
                        const hasNoteRow = editing || Boolean(note) || showLateTime;
                        const addNoteButton = canEdit && marked && !note && !editing ? (
                            <button
                                type="button"
                                onClick={() => setEditingNote(student.id)}
                                aria-label={`Thêm ghi chú cho ${name}`}
                                title="Thêm ghi chú"
                                className={cn(iconButton, 'hover:bg-ink-100 hover:text-ink-700')}
                            >
                                <Pencil className="h-4 w-4" />
                            </button>
                        ) : null;

                        return (
                            <div
                                key={student.id}
                                className={cn('grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-2 px-3 py-3 md:gap-4', GRID)}
                            >
                                <div className="min-w-0">
                                    <span className="font-medium text-ink-900 break-words">{name}</span>
                                    {student.isTemporary ? (
                                        <span className="ml-2 whitespace-nowrap rounded bg-academy-50 px-1.5 py-0.5 text-xs text-academy-800">Học bù</span>
                                    ) : null}
                                </div>

                                <div className="col-span-2 row-start-2 md:col-span-1 md:row-start-auto" title={markedAtTitle(student)}>
                                    <div
                                        role="radiogroup"
                                        aria-label={`Trạng thái của ${name}`}
                                        className="grid grid-cols-4 overflow-hidden rounded-lg border border-ink-300"
                                    >
                                        {STATUS_OPTIONS.map((option, index) => {
                                            const selected = student.status === option.value;
                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={selected}
                                                    disabled={!canEdit}
                                                    onClick={() => onStatusChange(student.id, option.value)}
                                                    className={cn(
                                                        'min-h-9 whitespace-nowrap px-3 text-sm transition-colors disabled:cursor-not-allowed',
                                                        index > 0 && 'border-l border-ink-300',
                                                        selected ? cn(option.active, 'font-semibold') : 'bg-white text-ink-700 hover:bg-ink-100 disabled:bg-ink-50 disabled:text-ink-400',
                                                    )}
                                                >
                                                    {option.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className={cn('col-span-2 flex min-w-0 items-center gap-2 md:col-span-1', !hasNoteRow && 'hidden md:flex')}>
                                    {showLateTime ? (
                                        <label className="flex shrink-0 items-center gap-1.5 text-sm text-ink-600">
                                            Đến lúc
                                            <input
                                                type="time"
                                                aria-label={`Giờ đến của ${name}`}
                                                value={toTimeValue(student.lateArrivalTime)}
                                                disabled={!canEdit}
                                                onChange={event => onDetailChange(student.id, { lateTime: event.target.value })}
                                                className={cn(fieldClass, 'w-[7.5rem]')}
                                            />
                                        </label>
                                    ) : null}
                                    {editing && noteField ? (
                                        <input
                                            autoFocus
                                            aria-label={`Ghi chú của ${name}`}
                                            placeholder={NOTE_PLACEHOLDER[student.status as RollCallStatus]}
                                            value={note}
                                            onChange={event => onDetailChange(student.id, { [noteField]: event.target.value })}
                                            onBlur={() => setEditingNote(null)}
                                            onKeyDown={event => { if (event.key === 'Enter' || event.key === 'Escape') setEditingNote(null); }}
                                            className={cn(fieldClass, 'w-full flex-1')}
                                        />
                                    ) : note ? (
                                        <>
                                            <span className="min-w-0 flex-1 truncate text-sm text-ink-700" title={note}>{note}</span>
                                            {canEdit ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingNote(student.id)}
                                                    aria-label={`Sửa ghi chú của ${name}`}
                                                    title="Sửa ghi chú"
                                                    className={cn(iconButton, 'hover:bg-ink-100 hover:text-ink-700')}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                            ) : null}
                                        </>
                                    ) : !marked ? (
                                        <span className="text-sm text-ink-400">Chưa điểm danh</span>
                                    ) : addNoteButton ? (
                                        <span className="hidden md:block">{addNoteButton}</span>
                                    ) : null}
                                </div>

                                <div className="col-start-2 row-start-1 flex items-center justify-end md:col-start-auto md:row-start-auto">
                                    {addNoteButton ? <span className="md:hidden">{addNoteButton}</span> : null}
                                    <button
                                        type="button"
                                        onClick={() => onAddAlert(student)}
                                        disabled={alertDisabled || alertBusyKey === alertKey}
                                        title="Thêm cảnh báo"
                                        aria-label={`Thêm cảnh báo cho ${name}`}
                                        className={cn(iconButton, 'hover:bg-warning-50 hover:text-warning-700 disabled:hover:bg-transparent')}
                                    >
                                        <Bell className="h-4 w-4" />
                                    </button>
                                    {student.isTemporary && canEdit && onRemoveMakeupStudent ? (
                                        <button
                                            type="button"
                                            onClick={() => onRemoveMakeupStudent(student.id)}
                                            title="Bỏ khỏi buổi học"
                                            aria-label={`Bỏ học sinh học bù ${name}`}
                                            className={cn(iconButton, 'hover:bg-danger-50 hover:text-danger-700')}
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {onAddMakeupStudent ? (
                <button
                    type="button"
                    onClick={onAddMakeupStudent}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-academy-700 hover:bg-academy-50"
                >
                    <Plus className="h-4 w-4" />
                    Thêm học sinh học bù
                </button>
            ) : null}
        </section>
    );
}
